/**
 * Globo del hero de /{locale}/shield.
 *
 * Sustituye al terminal que rotaba 12 filas cacheadas cada 4 segundos
 * reescribiéndoles la hora a «hace 0s» bajo una píldora que decía LIVE. Aquí
 * los eventos son reales y la hora es la de recepción.
 *
 * Cómo llegan los datos
 * ---------------------
 * El primer bloque viaja SERVIDO en el HTML (ShieldController), así que la
 * página funciona sin JavaScript, la caja nace con contenido y los enlaces a
 * las fichas de IP existen para un rastreador — que no espera a sondeos. A
 * partir de ahí se pide /abuse/globe.json, que devuelve una ventana FIJA de los
 * últimos eventos bajo una única clave de caché; el diff contra lo ya pintado
 * lo hace este fichero, que sabe qué ids tiene. Un cursor por visitante habría
 * dado una entrada de caché por persona y habría anulado el 304.
 *
 * La cadencia la manda el servidor (`ttl_seconds`), no está fijada aquí: si hay
 * que aflojar el sondeo no hace falta desplegar JavaScript.
 *
 * Las cuatro puertas, y por qué existen
 * -------------------------------------
 *  - **Por debajo de 768 px no se pinta nada.** Mismo criterio que la
 *    constelación MCP. Reproyectar ~3.100 vértices por fotograma en canvas 2D
 *    lo rasteriza la CPU, y el presupuesto de AGENTS.md (INP ≤ 200 ms) no
 *    perdona en gama baja. En móvil queda la lista, que es el contenido.
 *  - **`prefers-reduced-motion` no apaga el globo: lo congela.** Se pinta un
 *    fotograma quieto y se sigue pudiendo arrastrar. Apagarlo dejaría un hueco.
 *  - **Fuera de viewport o pestaña oculta, se para** el bucle Y el sondeo, y se
 *    aborta la petición en curso. Al volver se sondea de inmediato.
 *  - **El puntero NO dibuja.** `pointermove` solo acumula el giro; el repintado
 *    ocurre en el `requestAnimationFrame`. Dibujar dentro del manejador es el
 *    patrón de tarea larga que rompe INP.
 */

import { WORLD, CENTROIDS } from '../almc/shield-globe-world.js';

const D2R = Math.PI / 180;

/** Por debajo de esto no se pinta el canvas. Igual que mcp-constellation.js. */
const ANCHO_MINIMO = 768;

/** Tope de fotogramas por segundo. A 30 el giro se ve fluido y cuesta la mitad. */
const FPS = 30;

/** Densificación de las costas, en grados. Más fino no se nota y cuesta. */
const PASO_DENSIFICADO = 6;

/** Sede de ALMC (Lleida): destino de los arcos, que es donde llegan los reports. */
const DESTINO = [0.63, 41.62];

/** Arcos vivos como máximo. Con ~1,4 eventos/min no se alcanza salvo ráfaga. */
const MAX_ARCOS = 24;

/** Filas visibles en la columna. Las mismas que caben sin scroll. */
const MAX_FILAS = 12;

/** Sondeo: techo del backoff y factor, cuando no llega nada nuevo. */
const BACKOFF_FACTOR = 1.6;
const BACKOFF_TOPE_MS = 180000;

// ─────────────────────────────────────────────────────────────── utilidades

function aXyz(lonGr, latGr) {
    const a = lonGr * D2R;
    const b = latGr * D2R;
    const cb = Math.cos(b);
    return [cb * Math.sin(a), Math.sin(b), cb * Math.cos(a)];
}

/** Interpolación sobre la esfera: los arcos siguen el círculo máximo. */
function slerp(p, q, t) {
    const d = Math.max(-1, Math.min(1, p[0] * q[0] + p[1] * q[1] + p[2] * q[2]));
    const o = Math.acos(d);
    if (o < 1e-6) return p.slice();
    const s = Math.sin(o);
    const a = Math.sin((1 - t) * o) / s;
    const b = Math.sin(t * o) / s;
    return [a * p[0] + b * q[0], a * p[1] + b * q[1], a * p[2] + b * q[2]];
}

function edadTexto(iso) {
    if (!iso) return '';
    const seg = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
    if (seg < 60) return `hace ${seg}s`;
    if (seg < 3600) return `hace ${Math.floor(seg / 60)}m`;
    return `hace ${Math.floor(seg / 3600)}h`;
}

// ─────────────────────────────────────────── geometría, calculada una vez

/**
 * Las costas se densifican y se pasan a vectores unitarios UNA vez. Por
 * fotograma solo se rota y se proyecta: sin trigonometría de conversión.
 */
const ANILLOS = (() => {
    const salida = [];
    for (const anillo of WORLD) {
        const puntos = [];
        for (let j = 0; j < anillo.length; j++) {
            const a = anillo[j];
            const b = anillo[(j + 1) % anillo.length];
            const pa = aXyz(a[0], a[1]);
            const pb = aXyz(b[0], b[1]);
            const dot = Math.max(-1, Math.min(1, pa[0] * pb[0] + pa[1] * pb[1] + pa[2] * pb[2]));
            const pasos = Math.max(1, Math.ceil(Math.acos(dot) / (PASO_DENSIFICADO * D2R)));
            for (let s = 0; s < pasos; s++) puntos.push(slerp(pa, pb, s / pasos));
        }
        if (puntos.length > 3) salida.push(puntos);
    }
    return salida;
})();

const RETICULA = (() => {
    const lineas = [];
    for (let lon = -180; lon < 180; lon += 30) {
        const l = [];
        for (let lat = -80; lat <= 80; lat += 6) l.push(aXyz(lon, lat));
        lineas.push(l);
    }
    for (let lat = -60; lat <= 60; lat += 30) {
        const l = [];
        for (let lon = -180; lon <= 180; lon += 6) l.push(aXyz(lon, lat));
        lineas.push(l);
    }
    return lineas;
})();

const P_DESTINO = aXyz(DESTINO[0], DESTINO[1]);

// ───────────────────────────────────────────────────────────────── el globo

function crearGlobo(host, hint) {
    const canvas = document.createElement('canvas');
    canvas.className = 'sh-globe-canvas-el';
    canvas.tabIndex = 0;
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', host.dataset.globeLabel || 'Origen por país de las IPs bloqueadas');
    host.appendChild(canvas);

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return null;

    const reducido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let W = 0; let H = 0; let R = 0; let CX = 0; let CY = 0;
    let giro = 2.55;
    let inclinacion = 20 * D2R;
    let velocidad = 0;
    let arrastrando = false;
    const arcos = [];
    const nodos = new Map();
    const paleta = leerPaleta(host);

    function leerPaleta(el) {
        const s = getComputedStyle(el);
        const v = (n, def) => (s.getPropertyValue(n).trim() || def);
        return {
            oceano1: v('--sh-globe-ocean-1', '#143b58'),
            oceano2: v('--sh-globe-ocean-2', '#071523'),
            tierra: v('--sh-globe-land', '#2d7099'),
            costa: v('--sh-globe-coast', '#4e9cc4'),
            reticula: v('--sh-globe-grid', 'rgba(190,225,245,.11)'),
            evento: v('--sh-globe-event', '#ef6a5a'),
            destino: v('--sh-globe-home', '#2ea8e6'),
            limbo: v('--sh-globe-limb', '#24374e'),
        };
    }

    function medir() {
        const caja = host.getBoundingClientRect();
        if (!caja.width || !caja.height) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        W = caja.width;
        H = caja.height;
        canvas.width = Math.round(W * dpr);
        canvas.height = Math.round(H * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        R = Math.min(W, H) * 0.415;
        CX = W / 2;
        CY = H / 2;
    }

    // Rotación: primero sobre Y (el giro), luego sobre X (la inclinación).
    // Los cosenos se calculan una vez por fotograma, no una vez por vértice.
    let cg = 1; let sg = 0; let ct = 1; let st = 0;
    const tmp = [0, 0, 0];
    function rotar(p) {
        const x = p[0] * cg + p[2] * sg;
        const z = -p[0] * sg + p[2] * cg;
        tmp[0] = x;
        tmp[1] = p[1] * ct - z * st;
        tmp[2] = p[1] * st + z * ct;
        return tmp;
    }

    function pintarReticula() {
        ctx.strokeStyle = paleta.reticula;
        ctx.lineWidth = 0.7;
        for (const linea of RETICULA) {
            let trazando = false;
            ctx.beginPath();
            for (const p of linea) {
                rotar(p);
                if (tmp[2] < 0) { trazando = false; continue; }
                const x = CX + R * tmp[0];
                const y = CY - R * tmp[1];
                if (trazando) ctx.lineTo(x, y); else { ctx.moveTo(x, y); trazando = true; }
            }
            ctx.stroke();
        }
    }

    function pintarTierra() {
        ctx.fillStyle = paleta.tierra;
        ctx.strokeStyle = paleta.costa;
        ctx.lineWidth = 0.55;
        for (const anillo of ANILLOS) {
            let visible = false;
            ctx.beginPath();
            for (let j = 0; j < anillo.length; j++) {
                rotar(anillo[j]);
                let x; let y;
                if (tmp[2] >= 0) {
                    x = CX + R * tmp[0];
                    y = CY - R * tmp[1];
                    visible = true;
                } else {
                    // Los vértices del hemisferio oculto se anclan al limbo, o
                    // el relleno cruzaría el disco por el medio.
                    const m = Math.hypot(tmp[0], tmp[1]) || 1e-9;
                    x = CX + (R * tmp[0]) / m;
                    y = CY - (R * tmp[1]) / m;
                }
                if (j === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            }
            if (!visible) continue;
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
        }
    }

    function pintarArco(arco, ahora) {
        const edad = (ahora - arco.t0) / 1000;
        const crece = reducido ? 1 : Math.min(1, edad / 1.15);
        const alfa = reducido ? 0.5 : (edad < 1.15 ? 1 : Math.max(0, 1 - (edad - 1.15) / 2.6));
        if (alfa <= 0) return false;

        const hasta = Math.max(1, Math.floor(arco.puntos.length * crece));
        ctx.strokeStyle = paleta.evento;
        ctx.globalAlpha = alfa * 0.85;
        ctx.lineWidth = 1.5;
        ctx.lineCap = 'round';

        let trazando = false; let hx = 0; let hy = 0; let hayCabeza = false;
        ctx.beginPath();
        for (let i = 0; i < hasta; i++) {
            rotar(arco.suelo[i]);
            const visible = tmp[2] >= -0.05;
            rotar(arco.puntos[i]);
            if (!visible) { trazando = false; continue; }
            const x = CX + R * tmp[0];
            const y = CY - R * tmp[1];
            if (trazando) ctx.lineTo(x, y); else { ctx.moveTo(x, y); trazando = true; }
            hx = x; hy = y; hayCabeza = true;
        }
        ctx.stroke();

        if (hayCabeza && crece < 1) {
            ctx.globalAlpha = alfa;
            ctx.fillStyle = paleta.evento;
            ctx.beginPath();
            ctx.arc(hx, hy, 2.1, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.globalAlpha = 1;
        return true;
    }

    function pintarNodos() {
        for (const [cc, n] of nodos) {
            n.peso *= 0.9992;
            if (n.peso < 0.06) { nodos.delete(cc); continue; }
            rotar(n.p);
            if (tmp[2] < 0) continue;
            ctx.globalAlpha = 0.3 + Math.min(0.5, n.peso * 0.13);
            ctx.fillStyle = paleta.evento;
            ctx.beginPath();
            ctx.arc(CX + R * tmp[0], CY - R * tmp[1], 1.4 + Math.min(3.2, n.peso * 0.8), 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.globalAlpha = 1;
    }

    function pintarDestino(ahora) {
        rotar(P_DESTINO);
        if (tmp[2] < 0) return;
        const x = CX + R * tmp[0];
        const y = CY - R * tmp[1];
        const p = reducido ? 0.5 : ((ahora / 2400) % 1);
        ctx.strokeStyle = paleta.destino;
        ctx.lineWidth = 1;
        ctx.globalAlpha = 0.5 * (1 - p);
        ctx.beginPath();
        ctx.arc(x, y, 3 + p * 12, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.fillStyle = paleta.destino;
        ctx.beginPath();
        ctx.arc(x, y, 2.8, 0, Math.PI * 2);
        ctx.fill();
    }

    function pintar(ahora) {
        if (!W || !R) return;
        cg = Math.cos(giro); sg = Math.sin(giro);
        ct = Math.cos(inclinacion); st = Math.sin(inclinacion);

        ctx.clearRect(0, 0, W, H);

        const grad = ctx.createRadialGradient(CX - R * 0.32, CY - R * 0.38, R * 0.08, CX, CY, R);
        grad.addColorStop(0, paleta.oceano1);
        grad.addColorStop(1, paleta.oceano2);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(CX, CY, R, 0, Math.PI * 2);
        ctx.fill();

        pintarReticula();
        pintarTierra();

        ctx.save();
        ctx.beginPath();
        ctx.arc(CX, CY, R * 1.7, 0, Math.PI * 2);
        ctx.clip();
        for (let i = arcos.length - 1; i >= 0; i--) {
            if (!pintarArco(arcos[i], ahora)) arcos.splice(i, 1);
        }
        ctx.restore();

        pintarNodos();
        pintarDestino(ahora);

        ctx.strokeStyle = paleta.limbo;
        ctx.globalAlpha = 0.8;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(CX, CY, R, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;
    }

    // ── arrastre. El manejador NO dibuja: solo acumula. ──
    let ultimoX = 0; let ultimoY = 0; let ultimoT = 0; let pistaOculta = false;

    canvas.addEventListener('pointerdown', (e) => {
        arrastrando = true;
        velocidad = 0;
        ultimoX = e.clientX; ultimoY = e.clientY; ultimoT = e.timeStamp;
        try { canvas.setPointerCapture(e.pointerId); } catch (_) { /* sin captura, sigue funcionando */ }
        if (!pistaOculta && hint) { hint.classList.add('is-gone'); pistaOculta = true; }
    });

    canvas.addEventListener('pointermove', (e) => {
        if (!arrastrando) return;
        const dx = e.clientX - ultimoX;
        const dy = e.clientY - ultimoY;
        const dt = Math.max(1, e.timeStamp - ultimoT);
        giro += dx * 0.006;
        inclinacion = Math.max(-70 * D2R, Math.min(80 * D2R, inclinacion + dy * 0.004));
        velocidad = ((dx * 0.006) / dt) * 16;
        ultimoX = e.clientX; ultimoY = e.clientY; ultimoT = e.timeStamp;
    });

    const soltar = (e) => {
        if (!arrastrando) return;
        arrastrando = false;
        try { canvas.releasePointerCapture(e.pointerId); } catch (_) { /* ya liberado */ }
    };
    canvas.addEventListener('pointerup', soltar);
    canvas.addEventListener('pointercancel', soltar);

    // Teclado: sin esto el globo es inoperable sin ratón.
    canvas.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { giro -= 0.12; e.preventDefault(); }
        if (e.key === 'ArrowRight') { giro += 0.12; e.preventDefault(); }
        if (e.key === 'ArrowUp') { inclinacion = Math.min(80 * D2R, inclinacion + 0.08); e.preventDefault(); }
        if (e.key === 'ArrowDown') { inclinacion = Math.max(-70 * D2R, inclinacion - 0.08); e.preventDefault(); }
    });

    medir();
    if (typeof ResizeObserver === 'function') new ResizeObserver(medir).observe(host);

    return {
        canvas,
        avanzar(dt) {
            if (arrastrando) return;
            if (Math.abs(velocidad) > 0.0002) { giro += velocidad; velocidad *= 0.94; return; }
            if (!reducido) giro += dt * 0.00005;
        },
        pintar,
        estatico: reducido,
        /** Marca un país sin animar arco: para el bloque ya servido en el HTML. */
        sembrar(cc) {
            const c = CENTROIDS[cc];
            if (!c) return;
            const n = nodos.get(cc) || { p: aXyz(c[0], c[1]), peso: 0 };
            n.peso = Math.min(6, n.peso + 1);
            nodos.set(cc, n);
        },
        /** Evento nuevo: nodo + arco animado hacia la sede. */
        lanzar(cc) {
            const c = CENTROIDS[cc];
            if (!c) return;
            this.sembrar(cc);
            if (arcos.length >= MAX_ARCOS) return;
            const p0 = aXyz(c[0], c[1]);
            const suelo = []; const puntos = [];
            const N = 32;
            for (let i = 0; i <= N; i++) {
                const t = i / N;
                const s = slerp(p0, P_DESTINO, t);
                const alt = 1 + 0.19 * Math.sin(Math.PI * t);
                suelo.push(s);
                puntos.push([s[0] * alt, s[1] * alt, s[2] * alt]);
            }
            arcos.push({ suelo, puntos, t0: performance.now() });
        },
    };
}

// ──────────────────────────────────────────────────────────────── arranque

function boot() {
    const host = document.getElementById('sh-hero-globe');
    if (!host) return;

    const lista = host.querySelector('[data-globe-rows]');
    const src = host.dataset.globeSrc;
    const baseIp = host.dataset.globeIpBase || '';

    // Ids ya pintados por el servidor: no se vuelven a animar.
    const vistos = new Set();
    let ultimoId = 0;
    if (lista) {
        lista.querySelectorAll('li[data-id]').forEach((li) => {
            const id = parseInt(li.dataset.id, 10);
            if (Number.isFinite(id)) { vistos.add(id); ultimoId = Math.max(ultimoId, id); }
        });
    }

    // ── Puerta de móvil: la lista es el contenido, el globo es el adorno. ──
    const conCanvas = window.innerWidth >= ANCHO_MINIMO;
    let globo = null;

    if (conCanvas) {
        const caja = host.querySelector('[data-globe-canvas]');
        const hint = host.querySelector('[data-globe-hint]');
        if (caja) {
            globo = crearGlobo(caja, hint);
            if (globo) {
                host.classList.add('is-live');
                if (lista) {
                    lista.querySelectorAll('li[data-cc]').forEach((li) => globo.sembrar(li.dataset.cc));
                }
            }
        }
    }

    // ── Bucle de dibujo, con tope de fotogramas y pausas ──
    let enPantalla = true;
    let visible = !document.hidden;
    let ultimoFrame = performance.now();
    let acumulado = 0;
    const intervaloFrame = 1000 / FPS;
    let pintadoEstatico = false;

    function bucle(ahora) {
        requestAnimationFrame(bucle);
        if (!globo || !enPantalla || !visible) { ultimoFrame = ahora; return; }

        const dt = Math.min(120, ahora - ultimoFrame);
        ultimoFrame = ahora;

        // Con movimiento reducido se pinta UN fotograma y se para, salvo que el
        // usuario arrastre o llegue un evento: apagarlo del todo dejaría hueco.
        if (globo.estatico && pintadoEstatico) { globo.avanzar(dt); globo.pintar(ahora); return; }

        globo.avanzar(dt);
        acumulado += dt;
        if (acumulado < intervaloFrame) return;
        acumulado = 0;
        globo.pintar(ahora);
        pintadoEstatico = true;
    }
    requestAnimationFrame(bucle);

    if (globo && typeof IntersectionObserver === 'function') {
        new IntersectionObserver((ents) => { enPantalla = ents[0].isIntersecting; }, { threshold: 0.02 })
            .observe(globo.canvas);
    }

    // ── Sondeo ──
    if (!src) return;

    let temporizador = null;
    let abortador = null;
    let enVuelo = false;
    let esperaBase = 30000;
    let espera = esperaBase;

    /**
     * La fila se construye con el DOM, no con innerHTML.
     *
     * Los valores vienen de nuestro propio endpoint, pero `jail` es texto libre
     * de la configuración de fail2ban de cada cliente: entra en la base por la
     * API de ingesta y solo lo valida una regla de formato. En la landing de un
     * producto de seguridad, la ruta de datos de terceros hasta el DOM no pasa
     * por un parser de HTML.
     */
    function span(clase, texto) {
        const el = document.createElement('span');
        el.className = clase;
        el.textContent = texto;
        return el;
    }

    function contenidoFila(item) {
        const arriba = document.createElement('span');
        arriba.className = 'sh-globe-row-top';
        arriba.append(span('cc', item.cc), span('ip', item.ip));

        const edad = span('age', edadTexto(item.at));
        if (item.at) edad.dataset.at = item.at;

        const abajo = document.createElement('span');
        abajo.className = 'sh-globe-row-sub';
        abajo.append(span('jail', item.jail), edad);

        return [arriba, abajo];
    }

    function pintarFila(item) {
        if (!lista) return;
        const li = document.createElement('li');
        li.dataset.id = String(item.id);
        li.dataset.cc = item.cc;
        li.className = 'is-new';

        // `baseIp` sale de un atributo que escribe Blade con url(), asi que la
        // ruta es nuestra; lo unico variable es la IP, y va como texto.
        const envoltorio = document.createElement(baseIp ? 'a' : 'span');
        if (baseIp) {
            envoltorio.setAttribute('href', baseIp + encodeURIComponent(item.ip));
            envoltorio.setAttribute('title', `Ver el detalle de ${item.ip}`);
        }
        envoltorio.append(...contenidoFila(item));

        li.appendChild(envoltorio);
        lista.insertBefore(li, lista.firstChild);
        while (lista.children.length > MAX_FILAS) lista.removeChild(lista.lastElementChild);
        window.setTimeout(() => li.classList.remove('is-new'), 700);
    }

    function refrescarEdades() {
        if (!lista) return;
        lista.querySelectorAll('.age[data-at]').forEach((el) => {
            if (el.dataset.at) el.textContent = edadTexto(el.dataset.at);
        });
    }

    function programar(ms) {
        window.clearTimeout(temporizador);
        temporizador = window.setTimeout(sondear, ms);
    }

    async function sondear() {
        if (enVuelo || document.hidden) return;
        enVuelo = true;
        abortador = new AbortController();
        try {
            const res = await fetch(src, {
                signal: abortador.signal,
                headers: { Accept: 'application/json' },
                credentials: 'omit',
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const datos = await res.json();

            if (Number.isFinite(datos.ttl_seconds) && datos.ttl_seconds >= 5) {
                esperaBase = datos.ttl_seconds * 1000;
            }

            const nuevos = (datos.items || []).filter((it) => it && it.id > ultimoId && !vistos.has(it.id));
            for (const it of nuevos) { vistos.add(it.id); ultimoId = Math.max(ultimoId, it.id); }

            if (nuevos.length) {
                espera = esperaBase;
                // Se sueltan escalonados: de golpe se verían como un parpadeo.
                nuevos.forEach((it, i) => window.setTimeout(() => {
                    if (globo) globo.lanzar(it.cc);
                    pintarFila(it);
                }, i * 900));
            } else {
                // Sin novedades se afloja: a ~1,4 detecciones/min la mayoría de
                // sondeos vuelven vacíos y no tiene sentido insistir al mismo ritmo.
                espera = Math.min(BACKOFF_TOPE_MS, espera * BACKOFF_FACTOR);
            }
            refrescarEdades();
        } catch (err) {
            if (err.name !== 'AbortError') espera = Math.min(BACKOFF_TOPE_MS, espera * BACKOFF_FACTOR);
        } finally {
            enVuelo = false;
            abortador = null;
            if (!document.hidden) programar(espera);
        }
    }

    document.addEventListener('visibilitychange', () => {
        visible = !document.hidden;
        if (document.hidden) {
            window.clearTimeout(temporizador);
            if (abortador) abortador.abort();
        } else {
            espera = esperaBase;
            programar(0);   // al volver, sondeo inmediato: no se espera al tick
        }
    });

    programar(esperaBase);
    window.setInterval(refrescarEdades, 30000);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
    boot();
}
